import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./HttpUtils";

const API_BASE_URL = getApiBaseUrl();

const SALES_PRODUCT_BASE = `${API_BASE_URL}/shopify/sales-product`;

export type ShopifySalesProductOrdersSort =
  | "date_desc"
  | "date_asc"
  | "total_desc"
  | "total_asc";

export type ShopifySalesProductCustomersSort =
  | "total_orders_desc"
  | "total_orders_asc"
  | "total_spend_desc"
  | "total_spend_asc"
  | "sign_up_date_desc"
  | "sign_up_date_asc"
  | "recent_order_date_desc"
  | "recent_order_date_asc";

export function appendSalesProductTenantId(
  params: URLSearchParams,
  tenantId?: number,
): void {
  if (typeof tenantId === "number") {
    params.set("tenant_id", String(tenantId));
  }
}

/** Date-scoped endpoints: `start_date` and `end_date` are required (YYYY-MM-DD). */
export async function fetchShopifySalesProductOverview(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/overview?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductSalesSummary(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/sales/summary?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductSalesCards(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/sales/cards?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductSalesByChannel(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang; status?: string; metric?: "sales" | "orders" },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  if (options?.status && options.status !== "all") {
    params.set("status", options.status);
  }
  if (options?.metric) {
    params.set("metric", options.metric);
  }
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/sales/by-channel?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductSalesOrders(
  startDate: string,
  endDate: string,
  options?: {
    tenantId?: number;
    lang?: ApiLang;
    page?: number;
    pageSize?: number;
    sort?: ShopifySalesProductOrdersSort;
    search?: string;
    status?: string;
  },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
    page: String(options?.page ?? 1),
    page_size: String(options?.pageSize ?? 10),
    sort: options?.sort ?? "date_desc",
  });
  if (options?.search) {
    params.set("search", options.search);
  }
  if (options?.status && options.status !== "all") {
    params.set("status", options.status);
  }
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/sales/orders?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

/** Snapshot metrics — not filtered by order date. */
export async function fetchShopifySalesProductSalesOrderDetail(
  orderId: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/sales/orders/${encodeURIComponent(orderId)}?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductInventorySummary(
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/inventory/summary?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductInventoryByProduct(
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/inventory/by-product?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductInventoryCategories(
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/inventory/categories?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export type ShopifySalesProductInventorySort = "stock_desc" | "stock_asc";

export async function upsertShopifySalesProductDashboardSettings(
  options: { tenantId?: number; lang?: ApiLang; lowStockThreshold: number },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options.lang),
  });
  appendSalesProductTenantId(params, options.tenantId);
  const url = `${SALES_PRODUCT_BASE}/dashboard-settings?${params.toString()}`;
  const response = await fetch(url, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({
      low_stock_threshold: options.lowStockThreshold,
    }),
  });
  return handleJsonResponse<unknown>(response);
}

/** Paginated product rows — `page` min 1 (default 1), `page_size` 1–100 (default 10). */
export async function fetchShopifySalesProductInventoryProductsDetail(
  options?: {
    tenantId?: number;
    lang?: ApiLang;
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    category?: string;
    sort?: ShopifySalesProductInventorySort;
  },
): Promise<unknown> {
  const params = new URLSearchParams({
    lang: normalizeApiLang(options?.lang),
    page: String(options?.page ?? 1),
    page_size: String(Math.min(100, Math.max(1, options?.pageSize ?? 10))),
    sort: options?.sort ?? "stock_desc",
  });
  if (options?.search) {
    params.set("search", options.search);
  }
  if (options?.status) {
    params.set("status", options.status);
  }
  if (options?.category) {
    params.set("category", options.category);
  }
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/inventory/products-detail?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductCustomersSummary(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/customers/summary?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

/** Paginated customer list, including customers without orders. */
export async function fetchShopifySalesProductCustomersAll(
  startDate: string,
  endDate: string,
  options?: {
    tenantId?: number;
    lang?: ApiLang;
    page?: number;
    pageSize?: number;
    sort?: ShopifySalesProductCustomersSort;
    search?: string;
  },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
    page: String(options?.page ?? 1),
    page_size: String(Math.min(100, Math.max(1, options?.pageSize ?? 10))),
  });
  if (options?.sort) {
    params.set("sort", options.sort);
  }
  if (options?.search) {
    params.set("search", options.search);
  }
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/customers/list?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductFinancialsSummary(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/financials/summary?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductFinancialsMonthlyTrend(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/financials/monthly-trend?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductFinancialsByChannel(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/financials/by-channel?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export async function fetchShopifySalesProductFinancialsPnl(
  startDate: string,
  endDate: string,
  options?: { tenantId?: number; lang?: ApiLang },
): Promise<unknown> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(options?.lang),
  });
  appendSalesProductTenantId(params, options?.tenantId);
  const url = `${SALES_PRODUCT_BASE}/financials/pnl?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<unknown>(response);
}

export interface SparkPoint {
  label: string;
  current: number;
  previous: number;
  currentFormattedValue?: string;
  previousFormattedValue?: string;
  current_start_date?: string;
  current_end_date?: string;
  previous_start_date?: string;
  previous_end_date?: string;
}

export interface ShopifyKpiCard {
  key: "netRevenue" | "totalSales" | "orders" | "netQuantity" | "discount" | "returns" | "refunds";
  title: string;
  value: string;
  raw_value: number;
  raw_valueFormattedValue?: string;
  delta: number | null;
  delta_label: string;
  deltaFormattedValue?: string;
  comparison_label: string;
  current_date: string;
  previous_date: string;
  spark: SparkPoint[];
}

export interface CityRevenueItem {
  city: string;
  net_revenue: number;
  /** Pre-formatted amount from the API (e.g. "USD 2,088"), preferred over formatting `net_revenue` locally. */
  net_revenueFormattedValue?: string;
  customers?: number;
  customersFormattedValue?: string;
  customer_count?: number;
  orders?: number;
  ordersFormattedValue?: string;
  order_count?: number;
  return_value?: number;
  return_valueFormattedValue?: string;
  return_count?: number;
  return_countFormattedValue?: string;
  latitude?: number;
  longitude?: number;
}

export interface ShopifyHeatmap {
  day_labels: string[];
  hour_labels: string[];
  data: number[][];
}

export interface ShopifyTopProduct {
  name: string;
  value: number;
  /** Pre-formatted amount from the API (e.g. "USD 4,946"), preferred over formatting `value` locally. */
  valueFormattedValue?: string;
  color: string;
}

export interface ShopifyLineItemDiscount {
  value: number;
  value_formatted: string;
  delta: number | null;
  delta_label: string;
}

export type RegionId = "punjab" | "sindh" | "balochistan" | "kpk" | "ajk" | "federal" | "others";

export interface RegionPanel {
  id?: RegionId;
  title: string;
  kpis: ShopifyKpiCard[];
  cities: CityRevenueItem[];
  heatmap: ShopifyHeatmap;
  top_products: ShopifyTopProduct[];
}

/** Flat stacked cross-filter params for GET /api/shopify/sales-analytics/ */
export interface ShopifySalesCrossFilterRequest {
  filter_region?: string;
  filter_city?: string;
  filter_hour?: number;
  filter_weekday?: number;
}

export interface ShopifySalesActiveFilter {
  region?: string | null;
  city?: string | null;
  hour?: number | null;
  weekday?: number | null;
}

export interface ShopifySalesAnalyticsResponse {
  region?: string;
  currency: string;
  date_range_label?: string;
  comparison_period: { start_date: string; end_date: string };
  current_period: { start_date: string; end_date: string };
  active_filter?: ShopifySalesActiveFilter | null;
  regions: RegionPanel[];
  line_item_discount?: ShopifyLineItemDiscount;
  code?: string;
  message?: string;
}

function appendCrossFilterParams(
  params: URLSearchParams,
  cross: ShopifySalesCrossFilterRequest,
): void {
  const filterRegion = cross.filter_region?.trim();
  if (filterRegion && filterRegion !== "undefined") {
    params.set("filter_region", filterRegion);
  }
  const filterCity = cross.filter_city?.trim();
  if (filterCity) {
    params.set("filter_city", filterCity);
  }
  if (typeof cross.filter_hour === "number") {
    params.set("filter_hour", String(cross.filter_hour));
  }
  if (typeof cross.filter_weekday === "number") {
    params.set("filter_weekday", String(cross.filter_weekday));
  }
}

export async function fetchShopifySalesAnalytics(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  options?: {
    tenantId?: number;
    crossFilter?: ShopifySalesCrossFilterRequest | null;
  },
): Promise<ShopifySalesAnalyticsResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    lang: normalizeApiLang(lang),
  });
  if (typeof options?.tenantId === "number") {
    params.set("tenant_id", String(options.tenantId));
  }
  if (options?.crossFilter) {
    appendCrossFilterParams(params, options.crossFilter);
  }
  const url = `${API_BASE_URL}/shopify/sales-analytics/?${params.toString()}`;
  const response = await fetch(url, {
    method: "GET",
    headers: buildAuthHeaders("api_key"),
  });
  return handleJsonResponse<ShopifySalesAnalyticsResponse>(response);
}
